import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isPasswordExpired } from '../../services/passwordPolicy';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Array<'admin' | 'superviseur' | 'analyste' | 'agent_declarant' | 'enqueteur' | 'collaborateur_national' | 'collaborateur_international' | 'lecteur'>;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // 1️⃣ Loading state — AuthContext uniquement, pas de requête supplémentaire
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // 2️⃣ Not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3️⃣ Compte suspendu — forcer la déconnexion immédiate
  if ((user as any)?.statut === 'suspendu') {
    window.dispatchEvent(new Event('force-logout'));
    return <Navigate to="/login" replace />;
  }

  // 4️⃣ Force password change — données déjà dans le user de l'AuthContext
  const mustChange = (user as any)?.must_change_password;
  const expiryDays = (user as any)?.password_expiry_days || 90;
  const passwordChangedAt = (user as any)?.password_changed_at;
  const passwordExpired = isPasswordExpired(passwordChangedAt, expiryDays);
  const forceChange = mustChange || passwordExpired;

  if (forceChange && location.pathname !== '/force-change-password') {
    return <Navigate to="/force-change-password" replace />;
  }

  // 4️⃣ Role based access control
  // Guard against missing role property – treat it as unauthorized.
  const userRole = (user as any)?.role;
  if (allowedRoles && (!userRole || !allowedRoles.includes(userRole))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <i className="ri-error-warning-line text-6xl text-red-500 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600 mb-6">
            Vous n&apos;avez pas les permissions nécessaires pour accéder à cette page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  // 5️⃣ All checks passed – render children
  return <>{children}</>;
};